import React, { useState, useEffect } from 'react';
import { Gift, Truck, Wallet, ChevronRight } from 'lucide-react';
import { logger } from './utils/logger.js';
import botAPI from './botAPI.js';
import CategoryIcon from './CategoryIcon.jsx';
import CartIcon from './CartIcon.jsx';
import CharacterMessage from './components/CharacterMessage.jsx';
import GlobalHeader from './components/GlobalHeader';
import { Platform } from './utils/platform';

// Утилиты для работы с корзиной и акциями
const cartUtils = {
  // Получение цвета для категории продукта
  getCategoryColor: (category) => {
    const colors = {
      'red_caviar': 'bg-red-500',
      'fresh_fish': 'bg-blue-400',
      'salted_smoked_fish': 'bg-amber-700',
      'shrimp': 'bg-orange-500',
      'shrimps_langoustines': 'bg-orange-500',
      'mollusks': 'bg-cyan-600',
      'canned_products': 'bg-gray-600',
    };
    return colors[category] || 'bg-gray-300';
  },

  // Получение эмодзи для категории
  getCategoryEmoji: (category) => {
    const emojis = {
      'red_caviar': '🥄',
      'fresh_fish': '🐠',           // Тропическая рыбка для свежести
      'salted_smoked_fish': '🐡',   // Рыба фугу для уникальности
      'shrimp': '🦐',
      'shrimps_langoustines': '🦐',
      'mollusks': '🦑',
      'canned_products': '🥫',
    };
    return emojis[category] || '🐙';
  },

  // Форматирование цены
  formatPrice: (price) => {
    return `${price.toLocaleString('ru-RU')} ₽`;
  },

};

// Компонент карточки товара в корзине
const CartItem = ({ item, onUpdateQuantity, onRemove }) => {
  const handleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1) {
      onUpdateQuantity(item.id, newQuantity);
    }
  };

  return (
    <div className="flex items-center py-4 border-b border-white/[0.10] dark:border-white/[0.08]">
      {/* Изображение товара или иконка категории */}
      <div className="w-12 h-12 mr-3 shrink-0">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="w-full h-full object-cover rounded-lg"
            loading="lazy"
            onError={(e) => {
              // Если изображение не загрузилось, показываем иконку категории
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div
          className={`w-full h-full ${cartUtils.getCategoryColor(item.category_name || item.category)} rounded-lg flex items-center justify-center text-white`}
          style={{ display: item.image_url ? 'none' : 'flex' }}
        >
          <CategoryIcon category={item.category_name || item.category} size="small" />
        </div>
      </div>

      {/* Информация о товаре */}
      <div className="flex-grow">
        <div className="font-bold text-gray-900 dark:text-gray-100">{item.name}</div>
        <div className="text-sm text-gray-600 dark:text-gray-400">{item.weight || item.unit}</div>
      </div>

      {/* Элементы управления количеством */}
      <div className="flex items-center mx-2">
        <button
          onClick={() => handleQuantityChange(item.quantity - 1)}
          className="w-8 h-8 backdrop-blur-[4px] bg-gradient-to-br from-cyan-50/[0.15] to-blue-50/[0.08] dark:from-cyan-400/[0.15] dark:to-blue-400/[0.08] border border-white/[0.15] dark:border-white/[0.12] rounded-l flex items-center justify-center hover:from-blue-50/[0.25] hover:to-indigo-50/[0.15] dark:hover:from-blue-400/[0.25] dark:hover:to-indigo-400/[0.15] transition-all text-gray-900 dark:text-gray-100"
          style={{
            WebkitBackdropFilter: 'blur(16px)',
            backdropFilter: 'blur(16px)'
          }}
        >
          -
        </button>
        <div className="w-10 h-8 flex items-center justify-center border-y border-white/[0.15] dark:border-white/[0.12] text-gray-900 dark:text-gray-100">
          {item.quantity}
        </div>
        <button
          onClick={() => handleQuantityChange(item.quantity + 1)}
          className="w-8 h-8 backdrop-blur-[4px] bg-gradient-to-br from-cyan-50/[0.15] to-blue-50/[0.08] dark:from-cyan-400/[0.15] dark:to-blue-400/[0.08] border border-white/[0.15] dark:border-white/[0.12] rounded-r flex items-center justify-center hover:from-blue-50/[0.25] hover:to-indigo-50/[0.15] dark:hover:from-blue-400/[0.25] dark:hover:to-indigo-400/[0.15] transition-all text-gray-900 dark:text-gray-100"
          style={{
            WebkitBackdropFilter: 'blur(16px)',
            backdropFilter: 'blur(16px)'
          }}
        >
          +
        </button>
      </div>

      {/* Цена */}
      <div className="w-24 text-right font-bold text-gray-900 dark:text-gray-100">
        {cartUtils.formatPrice(item.price * item.quantity)}
      </div>

      {/* Кнопка удаления */}
      <button
        onClick={() => onRemove(item.id)}
        className="ml-3 text-red-500 hover:text-red-700 transition-colors"
        aria-label="Удалить товар"
      >
        ✕
      </button>
    </div>
  );
};

// Компонент баннера с рекомендациями
const RecommendationBanner = ({ cartItems, onAddToCart, allProducts }) => {
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    if (!allProducts || allProducts.length === 0) return;

    // Получаем ID товаров в корзине (приводим к числу для корректного сравнения)
    const cartItemIds = new Set(cartItems.map(item => Number(item.id || item.product_id)));

    // Фильтруем товары, которых нет в корзине
    // allProducts из API использует product_id, cartItems использует id
    const availableProducts = allProducts.filter(product => {
      const productId = Number(product.product_id || product.id);
      return !cartItemIds.has(productId) && product.is_active !== '0';
    });

    // Если нечего рекомендовать — пустой массив
    if (availableProducts.length === 0) {
      setRecommendations([]);
      return;
    }

    // Логика выбора рекомендаций
    const recommendedProducts = [];

    // 1. Рекомендуем товары из тех же категорий
    const cartCategories = new Set(cartItems.map(item => item.category_name || item.category));
    const sameCategory = availableProducts.filter(product =>
      cartCategories.has(product.category_name || product.category)
    );

    // Перемешиваем и берём до 2 товаров из той же категории
    const shuffledSameCategory = sameCategory.sort(() => Math.random() - 0.5).slice(0, 2);

    shuffledSameCategory.forEach(product => {
      recommendedProducts.push({
        ...product,
        id: product.product_id || product.id,
        name: product.product_name || product.name,
        reason: 'Часто покупают вместе'
      });
    });

    // 2. Добавляем товары из других категорий для разнообразия
    const otherCategories = availableProducts
      .filter(product => !cartCategories.has(product.category_name || product.category))
      .sort(() => Math.random() - 0.5)
      .slice(0, 3 - recommendedProducts.length);

    otherCategories.forEach(product => {
      recommendedProducts.push({
        ...product,
        id: product.product_id || product.id,
        name: product.product_name || product.name,
        reason: 'Попробуйте также'
      });
    });

    // Максимум 3 рекомендации
    setRecommendations(recommendedProducts.slice(0, 3));
  }, [cartItems, allProducts]);

  return recommendations.length > 0 ? (
    <div className="my-6 w-full">
      <h3 className="font-bold text-lg mb-3 text-gray-900 dark:text-gray-100">Рекомендуем добавить</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {recommendations.map((rec, index) => (
          <div
            key={rec.id || rec.product_id || `recommendation-${index}`}
            className="backdrop-blur-[4px] bg-gradient-to-br from-cyan-50/[0.10] to-blue-50/[0.03] dark:from-cyan-400/[0.10] dark:to-blue-400/[0.03] border border-white/[0.15] dark:border-white/[0.12] rounded-2xl p-3 flex items-center justify-between"
            style={{
              WebkitBackdropFilter: 'blur(16px)',
              backdropFilter: 'blur(16px)',
              boxShadow: '0 8px 24px rgba(99,102,241,0.15), inset 0 1px 0 rgba(255,255,255,0.15)'
            }}
          >
            <div>
              <div className="font-bold text-gray-900 dark:text-gray-100">{rec.name || rec.product_name}</div>
              <div className="text-sm text-gray-700 dark:text-gray-300">{rec.reason}</div>
              <div className="font-bold mt-1 text-gray-900 dark:text-gray-100">{cartUtils.formatPrice(parseFloat(rec.price || rec.finalPrice || 0))}</div>
            </div>
            <button
              onClick={() => onAddToCart({
                id: rec.id || rec.product_id,
                name: rec.name || rec.product_name,
                price: parseFloat(rec.price || rec.finalPrice || 0),
                weight: rec.weight || rec.unit || '',
                category: rec.category_name || rec.category || 'other',
                category_name: rec.category_name || rec.category || 'other',
                quantity: 1
              })}
              className="backdrop-blur-[4px] bg-gradient-to-br from-cyan-500/[0.65] to-blue-600/[0.75] dark:from-cyan-400/[0.65] dark:to-blue-500/[0.75] hover:from-cyan-500/[0.85] hover:to-blue-600/[0.90] text-white px-3 py-1 rounded-full text-sm transition-all font-bold border border-white/[0.20]"
              style={{
                WebkitBackdropFilter: 'blur(12px)',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 4px 12px rgba(99,102,241,0.25)',
                textShadow: '0 1px 4px rgba(0,0,0,0.3)'
              }}
            >
              Добавить
            </button>
          </div>
        ))}
      </div>
    </div>
  ) : null;
};

// Тиры доставки по сумме заказа (хардкод, позже из API)
const DELIVERY_TIERS = [
  { minSum: 0, fee: 300, label: '300 ₽' },
  { minSum: 3000, fee: 200, label: '200 ₽' },
  { minSum: 7000, fee: 100, label: '100 ₽' },
  { minSum: 10000, fee: 0, label: 'Бесплатно' }
];

// Расчёт доставки по сумме заказа
const getDeliveryFee = (orderSum) => {
  for (let i = DELIVERY_TIERS.length - 1; i >= 0; i--) {
    if (orderSum >= DELIVERY_TIERS[i].minSum) {
      return DELIVERY_TIERS[i];
    }
  }
  return DELIVERY_TIERS[0];
};

// Следующий тир доставки (для прогрессбара)
const getNextDeliveryTier = (orderSum) => {
  for (let i = 0; i < DELIVERY_TIERS.length; i++) {
    if (orderSum < DELIVERY_TIERS[i].minSum) {
      return DELIVERY_TIERS[i];
    }
  }
  return null; // Уже на максимальном тире
};

// Секция бонусов и доставки в корзине
const CartBonusSection = ({ totalSum, userBalance = 0, isFirstOrder = false }) => {
  const currentTier = getDeliveryFee(totalSum);
  const nextTier = getNextDeliveryTier(totalSum);
  const amountToNextTier = nextTier ? nextTier.minSum - totalSum : 0;

  return (
    <div
      className="backdrop-blur-[4px] bg-gradient-to-br from-cyan-50/[0.10] to-blue-50/[0.03] dark:from-cyan-400/[0.10] dark:to-blue-400/[0.03] border border-white/[0.15] dark:border-white/[0.12] rounded-2xl p-4 mb-4 w-full"
      style={{
        WebkitBackdropFilter: 'blur(16px)',
        backdropFilter: 'blur(16px)',
        boxShadow: '0 8px 24px rgba(99,102,241,0.12), inset 0 1px 0 rgba(255,255,255,0.15)'
      }}
    >
      {/* Баланс (если > 0) */}
      {userBalance > 0 && (
        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-white/[0.10] dark:border-white/[0.08]">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-400/20 to-blue-500/10 flex items-center justify-center shrink-0">
            <Wallet size={18} className="text-sky-600 dark:text-sky-400" />
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Ваш баланс: {userBalance.toLocaleString('ru-RU')} ₽
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Можно списать при оформлении
            </div>
          </div>
        </div>
      )}

      {/* Приветственный бонус (если первый заказ) */}
      {isFirstOrder && (
        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-white/[0.10] dark:border-white/[0.08]">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400/20 to-green-500/10 flex items-center justify-center shrink-0">
            <Gift size={18} className="text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Приветственный бонус 5%
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              +{Math.round(totalSum * 0.05).toLocaleString('ru-RU')} ₽ на баланс после получения
            </div>
          </div>
        </div>
      )}

      {/* Доставка */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-400/20 to-blue-500/10 flex items-center justify-center shrink-0">
          <Truck size={18} className="text-sky-600 dark:text-sky-400" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Доставка: {currentTier.label}
          </div>
          {nextTier && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Ещё {amountToNextTier.toLocaleString('ru-RU')} ₽ до {nextTier.fee === 0 ? 'бесплатной доставки' : `доставки за ${nextTier.label}`}
            </div>
          )}
          {!nextTier && totalSum >= 10000 && (
            <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
              Бесплатная доставка + бонус до 5%
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Компонент формы оформления заказа
const CheckoutForm = ({ isVisible, onClose, onSubmit, telegramUser, savedContact }) => {
  // Инициализируем из сохранённого профиля или данных платформы
  const initialName = savedContact
    ? `${savedContact.first_name || ''} ${savedContact.last_name || ''}`.trim()
    : telegramUser
      ? `${telegramUser.firstName || telegramUser.first_name || ''} ${telegramUser.lastName || telegramUser.last_name || ''}`.trim()
      : '';
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState(savedContact?.phone || '');
  const [address, setAddress] = useState(savedContact?.delivery_address || '');
  const [nameError, setNameError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [addressError, setAddressError] = useState('');
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [isSubscribedToPromo, setIsSubscribedToPromo] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Автозаполнение из профиля (приоритет — данные с сервера актуальнее localStorage)
  React.useEffect(() => {
    if (savedContact) {
      const profileName = `${savedContact.first_name || ''} ${savedContact.last_name || ''}`.trim();
      if (profileName) setName(profileName);
      if (savedContact.phone) setPhone(savedContact.phone);
      if (savedContact.delivery_address) setAddress(savedContact.delivery_address);
    }
  }, [savedContact]);

  // Fallback: имя из платформы, телефон из localStorage
  React.useEffect(() => {
    if (telegramUser && !name) {
      const newName = `${telegramUser.firstName || telegramUser.first_name || ''} ${telegramUser.lastName || telegramUser.last_name || ''}`.trim();
      if (newName) setName(newName);
    }
    const savedPhone = localStorage.getItem('saved_phone');
    if (savedPhone && !phone) {
      setPhone(savedPhone);
    }
  }, [telegramUser]);

  // Закрытие по Escape (a11y)
  React.useEffect(() => {
    if (!isVisible) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, onClose]);

  // Форматирование телефона
  const formatPhone = (value) => {
    // Убираем все символы кроме цифр
    let digits = value.replace(/\D/g, '');

    // Если начинается с 8, меняем на 7
    if (digits.startsWith('8') && digits.length === 11) {
      digits = '7' + digits.substring(1);
    }

    // Если введен код без +7 (10 цифр), добавляем 7 в начало
    if (digits.length === 10 && !digits.startsWith('7')) {
      digits = '7' + digits;
    }

    // Если нет кода страны, добавляем 7
    if (digits.length === 10) {
      digits = '7' + digits;
    }

    // Форматируем в виде +7(XXX)XXX-XX-XX
    if (digits.length >= 11 && digits.startsWith('7')) {
      const formatted = `+7(${digits.substring(1, 4)})${digits.substring(4, 7)}-${digits.substring(7, 9)}-${digits.substring(9, 11)}`;
      return formatted;
    }

    return value;
  };

  // Валидация телефона
  const validatePhone = (value) => {
    // Убираем все символы кроме цифр
    const phoneDigits = value.replace(/\D/g, '');

    // После нормализации должно быть 11 цифр, начиная с 7
    if (phoneDigits.length === 11 && phoneDigits.startsWith('7')) {
      setPhoneError('');
      return true;
    }

    // Если 10 цифр, это тоже допустимо (добавим 7 при форматировании)
    if (phoneDigits.length === 10) {
      setPhoneError('');
      return true;
    }

    setPhoneError('Введите корректный номер телефона');
    return false;
  };

  // Валидация имени
  const validateName = (value) => {
    if (value.trim().length < 2) {
      setNameError('Введите ваше имя');
      return false;
    }

    setNameError('');
    return true;
  };

  // Валидация адреса
  const validateAddress = (value) => {
    if (value.trim().length < 10) {
      setAddressError('Укажите полный адрес доставки');
      return false;
    }

    setAddressError('');
    return true;
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    if (nameError) validateName(value);
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;

    // Извлекаем только цифры
    let digits = value.replace(/\D/g, '');

    // Нормализуем: 8 → 7
    if (digits.startsWith('8')) {
      digits = '7' + digits.slice(1);
    }

    // Ограничиваем максимум 11 цифр
    if (digits.length > 11) {
      return;
    }

    // Форматируем: +7(999)123-45-67
    let formatted = '';
    if (digits.length > 0) {
      if (digits.startsWith('7')) {
        formatted = '+7';
        if (digits.length > 1) {
          formatted += `(${digits.slice(1, 4)}`;
        }
        if (digits.length >= 4) {
          formatted += ')';
        }
        if (digits.length >= 5) {
          formatted += `${digits.slice(4, 7)}`;
        }
        if (digits.length >= 8) {
          formatted += `-${digits.slice(7, 9)}`;
        }
        if (digits.length >= 10) {
          formatted += `-${digits.slice(9, 11)}`;
        }
      } else {
        // Если не начинается с 7, добавляем +7 автоматически
        formatted = `+7(${digits.slice(0, 3)}`;
        if (digits.length >= 3) {
          formatted += ')';
        }
        if (digits.length >= 4) {
          formatted += `${digits.slice(3, 6)}`;
        }
        if (digits.length >= 7) {
          formatted += `-${digits.slice(6, 8)}`;
        }
        if (digits.length >= 9) {
          formatted += `-${digits.slice(8, 10)}`;
        }
      }
    }

    setPhone(formatted);
    if (phoneError) validatePhone(formatted);
  };

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setAddress(value);
    if (addressError) validateAddress(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Используем введенное имя
    const finalName = name.trim();

    // Форматируем телефон перед отправкой
    const formattedPhone = formatPhone(phone);

    // Валидация формы
    const isNameValid = validateName(finalName);
    const isPhoneValid = validatePhone(formattedPhone);
    const isAddressValid = validateAddress(address);

    if (!isNameValid || !isPhoneValid || !isAddressValid) {
      return;
    }

    setIsSubmitting(true);

    // Отладочная информация
    logger.log('📝 Данные формы перед отправкой:', {
      name: finalName,
      phone: formattedPhone,
      address: address.trim(),
      isOrganizer: isOrganizer,
      isSubscribedToPromo: isSubscribedToPromo
    });

    try {
      // Отправляем согласие на обработку персональных данных
      const userId = Platform.getUser()?.id;
      if (userId && privacyAccepted) {
        try {
          await botAPI.sendPrivacyConsent(userId);
          logger.log('✅ Согласие на обработку ПД отправлено');
        } catch (privacyErr) {
          console.error('❌ Не удалось сохранить согласие на ПД:', privacyErr);
          setError('Не удалось сохранить согласие на обработку данных. Попробуйте ещё раз.');
          setIsSubmitting(false);
          return;
        }
      }

      await onSubmit({
        name: finalName,
        phone: formattedPhone,
        address: address.trim(),
        isOrganizer: isOrganizer,
        isSubscribedToPromo: isSubscribedToPromo
      });
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[200] animate-fadeIn overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkout-form-title"
    >
      <div
        className="min-h-[100dvh] flex items-start justify-center px-4 pt-12 pb-4"
        onClick={(e) => {
          // Убираем клавиатуру при клике на фон формы (не на поля ввода)
          if (e.target === e.currentTarget) {
            document.activeElement?.blur();
          }
        }}
      >
        <div
          className="w-full max-w-md shadow-xl animate-scaleIn"
          onClick={(e) => {
            // Убираем клавиатуру при клике на область формы (не на поля ввода)
            if (e.target === e.currentTarget) {
              document.activeElement?.blur();
            }
          }}
        >
          <div
            className="relative backdrop-blur-[4px] bg-gradient-to-br from-cyan-500/[0.80] to-blue-600/[0.70] dark:from-cyan-400/[0.80] dark:to-blue-500/[0.70] text-white p-5 rounded-t-2xl border-b border-white/[0.15]"
            style={{
              WebkitBackdropFilter: 'blur(16px)',
              backdropFilter: 'blur(16px)',
              boxShadow: '0 8px 24px rgba(99,102,241,0.3)'
            }}
          >
            <h2 id="checkout-form-title" className="text-xl font-bold pr-8">Оформление заказа</h2>
            <button
              type="button"
              onClick={onClose}
              className="absolute top-5 right-5 text-white hover:text-gray-200 text-2xl font-bold leading-none transition-colors"
              aria-label="Закрыть"
            >
              ×
            </button>
          </div>
          <form
            onSubmit={handleSubmit}
            className="backdrop-blur-[4px] bg-gradient-to-br from-cyan-50/[0.10] to-blue-50/[0.03] dark:from-cyan-400/[0.10] dark:to-blue-400/[0.03] border border-white/[0.15] dark:border-white/[0.12] border-t-0 p-4 rounded-b-2xl overflow-y-auto"
            style={{
              maxHeight: 'calc(100dvh - 120px)',
              WebkitBackdropFilter: 'blur(16px)',
              backdropFilter: 'blur(16px)',
              boxShadow: '0 8px 24px rgba(99,102,241,0.15)'
            }}
          >

          <div className="mb-4">
            <label className="block text-sm font-medium text-white mb-2">
              Телефон для связи <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="+7(999)123-45-67"
                className={`flex-1 px-3 py-2 backdrop-blur-[4px] bg-white/[0.30] dark:bg-gray-800/[0.30] border ${
                  phoneError ? 'border-red-500' : 'border-white/[0.20] dark:border-white/[0.15]'
                } rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 placeholder:text-gray-500 text-white`}
                style={{
                  WebkitBackdropFilter: 'blur(12px)',
                  backdropFilter: 'blur(12px)'
                }}
                disabled={isSubmitting}
                required
              />
              {Platform.isTelegram() && (
                <button
                  type="button"
                  onClick={() => {
                    Platform.requestContact((success, data) => {
                      if (success && data) {
                        setPhone(data.phone_number);
                        // Сохраняем телефон в localStorage для автозаполнения
                        localStorage.setItem('saved_phone', data.phone_number);
                        Platform.hapticFeedback('success');
                      } else {
                        Platform.showAlert('Не удалось получить номер телефона');
                        Platform.hapticFeedback('error');
                      }
                    });
                  }}
                  className="px-4 py-2 backdrop-blur-[4px] bg-gradient-to-br from-blue-500/[0.40] to-cyan-500/[0.30] border border-white/[0.20] rounded-lg text-white text-sm font-medium hover:from-blue-500/[0.50] hover:to-cyan-500/[0.40] transition-all whitespace-nowrap"
                  style={{
                    WebkitBackdropFilter: 'blur(12px)',
                    backdropFilter: 'blur(12px)'
                  }}
                  disabled={isSubmitting}
                  title="Взять номер из Telegram"
                >
                  📱 Telegram
                </button>
              )}
            </div>
            {phoneError && (
              <p role="alert" className="mt-1 text-sm text-red-500">{phoneError}</p>
            )}
            {!phoneError && (
              <p className="mt-1 text-xs text-white/70">Можете вводить номер в любом формате</p>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-white mb-2">
              Адрес доставки <span className="text-red-500">*</span>
            </label>
            <textarea
              value={address}
              onChange={handleAddressChange}
              placeholder="Улица, дом, квартира, подъезд, этаж"
              rows={3}
              className={`w-full px-3 py-2 backdrop-blur-[4px] bg-white/[0.30] dark:bg-gray-800/[0.30] border ${
                addressError ? 'border-red-500' : 'border-white/[0.20] dark:border-white/[0.15]'
              } rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 placeholder:text-gray-500 text-white`}
              style={{
                WebkitBackdropFilter: 'blur(12px)',
                backdropFilter: 'blur(12px)'
              }}
              disabled={isSubmitting}
              required
            />
            {addressError && (
              <p role="alert" className="mt-1 text-sm text-red-500">{addressError}</p>
            )}
          </div>

          {/* Чекбокс заинтересованности в роли организатора */}
          <div
            className="mb-3 p-3 backdrop-blur-[4px] bg-gradient-to-br from-cyan-50/[0.10] to-blue-50/[0.03] dark:from-cyan-400/[0.10] dark:to-blue-400/[0.03] border border-white/[0.15] dark:border-white/[0.12] rounded-lg"
            style={{
              WebkitBackdropFilter: 'blur(12px)',
              backdropFilter: 'blur(12px)'
            }}
          >
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isOrganizer}
                onChange={(e) => {
                  logger.log('🟡 Интерес к организаторству изменен на:', e.target.checked);
                  setIsOrganizer(e.target.checked);
                }}
                className="mr-2 w-5 h-5 text-cyan-600 bg-white border-gray-300 rounded focus:ring-2 focus:ring-cyan-500 cursor-pointer shrink-0"
                disabled={isSubmitting}
              />
              <div className="text-sm">
                <span className="font-medium text-white">
                  Интересует роль организатора в районе
                </span>
                <div className="text-xs text-white/70 mt-0.5">
                  Свяжемся с вами для обсуждения условий сотрудничества
                </div>
              </div>
            </label>
          </div>

          {/* Чекбокс подписки на рассылку - КОМПАКТНАЯ ВЕРСИЯ */}
          <div
            className="mb-4 p-3 backdrop-blur-[4px] bg-gradient-to-br from-cyan-50/[0.10] to-blue-50/[0.03] dark:from-cyan-400/[0.10] dark:to-blue-400/[0.03] border border-white/[0.15] dark:border-white/[0.12] rounded-lg"
            style={{
              WebkitBackdropFilter: 'blur(12px)',
              backdropFilter: 'blur(12px)'
            }}
          >
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isSubscribedToPromo}
                onChange={(e) => {
                  logger.log('🔵 Рассылка чекбокс изменен на:', e.target.checked);
                  setIsSubscribedToPromo(e.target.checked);
                }}
                className="mr-2 w-5 h-5 text-cyan-600 bg-white border-gray-300 rounded focus:ring-2 focus:ring-cyan-500 cursor-pointer shrink-0"
                disabled={isSubmitting}
              />
              <div className="text-sm">
                <span className="font-medium text-white">
                  Получать акции и спецпредложения
                </span>
                <span className="text-white/60 ml-1">(не чаще 2 раз/нед)</span>
              </div>
            </label>
          </div>

          {/* Поле имени всегда редактируемое */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-white mb-2">
              Заказ для <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={handleNameChange}
              placeholder="Ваше имя"
              className={`w-full px-3 py-2 backdrop-blur-[4px] bg-white/[0.30] dark:bg-gray-800/[0.30] border ${
                nameError ? 'border-red-500' : 'border-white/[0.20] dark:border-white/[0.15]'
              } rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 placeholder:text-gray-500 text-white`}
              style={{
                WebkitBackdropFilter: 'blur(12px)',
                backdropFilter: 'blur(12px)'
              }}
              disabled={isSubmitting}
              required
            />
            {nameError && (
              <p role="alert" className="mt-1 text-sm text-red-500">{nameError}</p>
            )}
            <p className="mt-1 text-xs text-white/70">Заполните или измените данные</p>
          </div>

          {/* Согласие на обработку персональных данных */}
          <div
            className="mb-4 p-3 backdrop-blur-[4px] bg-gradient-to-br from-cyan-50/[0.10] to-blue-50/[0.03] dark:from-cyan-400/[0.10] dark:to-blue-400/[0.03] border border-white/[0.15] dark:border-white/[0.12] rounded-lg"
            style={{
              WebkitBackdropFilter: 'blur(12px)',
              backdropFilter: 'blur(12px)'
            }}
          >
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                className="mr-2 mt-0.5 w-5 h-5 text-cyan-600 bg-white border-gray-300 rounded focus:ring-2 focus:ring-cyan-500 cursor-pointer shrink-0"
                disabled={isSubmitting}
              />
              <div className="text-sm">
                <span className="font-medium text-white">
                  Я согласен на{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-cyan-300 hover:text-cyan-200"
                    onClick={(e) => e.stopPropagation()}
                  >
                    обработку персональных данных
                  </a>
                </span>
              </div>
            </label>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 backdrop-blur-[4px] bg-gradient-to-br from-gray-200/[0.60] to-gray-300/[0.50] dark:from-gray-700/[0.60] dark:to-gray-800/[0.50] hover:from-gray-300/[0.70] hover:to-gray-400/[0.60] dark:hover:from-gray-600/[0.70] dark:hover:to-gray-700/[0.60] border border-white/[0.15] text-gray-900 dark:text-gray-100 font-bold py-3 px-4 rounded-lg transition-all"
              style={{
                WebkitBackdropFilter: 'blur(12px)',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 4px 12px rgba(107,114,128,0.15)'
              }}
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !privacyAccepted}
              className="flex-1 bg-gradient-to-br from-green-600/[0.70] to-emerald-600/[0.60] hover:from-green-600/[0.85] hover:to-emerald-600/[0.75] text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                boxShadow: '0 4px 12px rgba(34,197,94,0.25)'
              }}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Оформляем...
                </>
              ) : (
                <>
                  <span className="mr-2">✓</span> Подтвердить заказ
                </>
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

// Компонент подтверждения заказа
const OrderConfirmation = ({ isVisible, onClose, cartItems, totalSum, bonusAmount = 0 }) => {
  // Закрытие по Escape (a11y)
  React.useEffect(() => {
    if (!isVisible) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-confirmation-title"
    >
      <div
        className="backdrop-blur-[4px] bg-gradient-to-br from-cyan-50/[0.10] to-blue-50/[0.03] dark:from-cyan-400/[0.10] dark:to-blue-400/[0.03] border border-white/[0.15] dark:border-white/[0.12] rounded-2xl w-full max-w-md shadow-xl overflow-hidden animate-scaleIn"
        style={{
          WebkitBackdropFilter: 'blur(16px)',
          backdropFilter: 'blur(16px)',
          boxShadow: '0 8px 24px rgba(99,102,241,0.2), inset 0 1px 0 rgba(255,255,255,0.2)'
        }}
      >
        <div
          className="p-5 border-b border-white/[0.15] backdrop-blur-[4px] bg-gradient-to-br from-green-600/[0.80] to-emerald-600/[0.70] dark:from-green-500/[0.80] dark:to-emerald-500/[0.70] text-white"
          style={{
            WebkitBackdropFilter: 'blur(16px)',
            backdropFilter: 'blur(16px)'
          }}
        >
          <div className="flex items-center">
            <div className="w-12 h-12 rounded-full bg-white text-green-600 flex items-center justify-center mr-4">
              <span className="text-2xl">✓</span>
            </div>
            <h2 id="order-confirmation-title" className="text-2xl font-bold" role="status" aria-live="polite">Заказ оформлен!</h2>
          </div>
        </div>

        <div className="p-5">
          <p className="text-lg mb-4 text-gray-900 dark:text-gray-100">Спасибо за заказ! Ваш заказ успешно оформлен.</p>

          <div
            className="backdrop-blur-[4px] bg-gradient-to-br from-gray-100/[0.30] to-gray-200/[0.20] dark:from-gray-800/[0.30] dark:to-gray-900/[0.20] border border-white/[0.15] p-3 rounded-lg mb-4"
            style={{
              WebkitBackdropFilter: 'blur(12px)',
              backdropFilter: 'blur(12px)'
            }}
          >
            <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">Сумма заказа:</div>
            <div className="font-bold text-xl text-gray-900 dark:text-gray-100">{cartUtils.formatPrice(totalSum)}</div>

            {bonusAmount > 0 && (
              <div className="text-sm text-sky-600 dark:text-sky-400 mt-2">
                +{cartUtils.formatPrice(bonusAmount)} вернётся на баланс после получения
              </div>
            )}
          </div>

          <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
            Мы свяжемся с вами в ближайшее время для уточнения деталей доставки.
          </p>

          <button
            onClick={onClose}
            className="w-full bg-gradient-to-br from-green-600/[0.70] to-emerald-600/[0.60] hover:from-green-600/[0.85] hover:to-emerald-600/[0.75] text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center transition-all"
            style={{
              boxShadow: '0 4px 12px rgba(34,197,94,0.25)'
            }}
          >
            <span className="mr-2">👍</span> Отлично!
          </button>
        </div>
      </div>
    </div>
  );
};

// Основной компонент корзины
const CartComponent = ({
  cartItems = [],
  onUpdateQuantity,
  onRemove,
  onAddToCart,
  onGoToCatalog,
  onClearCart,
  telegramUser,
  allProducts = [],
  onCheckout,
  // Для GlobalHeader навигации
  currentPage = 'cart',
  onNavigate,
  cartItemsCount = 0,
  // Приветственная скидка (из App.jsx, загружается при инициализации)
  isFirstOrder: isFirstOrderProp = false
}) => {
  // Состояние модального окна подтверждения заказа
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  // Состояние модального окна формы оформления
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  // Состояние промокода
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [isCheckingPromo, setIsCheckingPromo] = useState(false);
  // Состояние реферальной ссылки
  const [referralLink, setReferralLink] = useState('');
  // Состояние баланса и профиля
  const [userBalance, setUserBalance] = useState(0);
  const [savedContact, setSavedContact] = useState(null);
  // Состояние списания бонусов
  const [useBalance, setUseBalance] = useState(false);
  const [balanceToUse, setBalanceToUse] = useState(0);
  // isFirstOrder приходит из App.jsx как проп (уже загружен при инициализации)
  const isFirstOrder = isFirstOrderProp;

  // Загрузка реферальной ссылки и баланса при монтировании
  useEffect(() => {
    if (!telegramUser?.id) return;

    const loadUserData = async () => {
      try {
        const [referralResponse, profileResponse] = await Promise.all([
          botAPI.getUserReferral(telegramUser.id),
          botAPI.getUserProfile(telegramUser.id)
        ]);

        if (referralResponse.success && referralResponse.data?.referral_link) {
          setReferralLink(referralResponse.data.referral_link);
        }

        if (profileResponse?.success) {
          setUserBalance(profileResponse.bonuses?.current_balance || 0);
          // Сохраняем контактные данные из профиля для автозаполнения формы заказа
          if (profileResponse.user) {
            setSavedContact(profileResponse.user);
          }
        }
      } catch (error) {
        logger.log('Не удалось загрузить данные пользователя:', error);
      }
    };

    loadUserData();
  }, [telegramUser?.id]);

  // Прокрутка вверх при загрузке компонента
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Нормализация цен в товарах (поддержка разных полей: price, current_price, basePricePerKg)
  const normalizedCartItems = cartItems.map(item => ({
    ...item,
    price: item.price || item.current_price || item.basePricePerKg || 0
  }));

  // Расчет общей суммы
  const totalSum = normalizedCartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  // Максимальная сумма списания бонусов (30% от суммы товаров, но не больше баланса)
  const maxBalanceAllowed = Math.min(userBalance, Math.round(totalSum * 0.3));

  // Автосброс суммы списания при изменении корзины/баланса
  useEffect(() => {
    if (useBalance) {
      const capped = Math.min(balanceToUse, maxBalanceAllowed);
      if (capped !== balanceToUse) setBalanceToUse(capped);
    }
  }, [totalSum, userBalance]);

  // Сброс при выключении тогла
  useEffect(() => {
    if (!useBalance) setBalanceToUse(0);
  }, [useBalance]);

  // Обработчики событий
  const handleUpdateQuantity = (id, newQuantity) => {
    if (onUpdateQuantity) onUpdateQuantity(id, newQuantity);
  };

  const handleRemoveItem = (id) => {
    if (onRemove) onRemove(id);
  };

  const handleAddToCart = (product) => {
    if (onAddToCart) onAddToCart(product, product.quantity || 1);
  };

  // Гостевой режим — показать предложение авторизации
  const [showGuestAuthPrompt, setShowGuestAuthPrompt] = useState(false);

  // Закрытие промпта авторизации по Escape (a11y) — ПОСЛЕ useState (иначе TDZ-краш корзины)
  useEffect(() => {
    if (!showGuestAuthPrompt) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setShowGuestAuthPrompt(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showGuestAuthPrompt]);

  const handleCheckout = () => {
    // Если гость (нет user id) — предлагаем авторизоваться
    if (!telegramUser?.id) {
      setShowGuestAuthPrompt(true);
      return;
    }
    // Показываем форму оформления заказа
    setShowCheckoutForm(true);
  };

  // Обработчик кнопки "Пригласите соседей"
  const handleInviteNeighbors = () => {
    if (!referralLink) {
      logger.log('Реферальная ссылка не загружена');
      return;
    }

    try {
      const shareUrl = referralLink;
      const shareText = 'Присоединяйтесь к коллективному заказу морепродуктов! Чем больше нас, тем лучше цены для всех';

      if (navigator.share) {
        navigator.share({ title: 'Путь рыбы', text: shareText, url: shareUrl }).catch(() => {});
      } else {
        navigator.clipboard.writeText(shareUrl);
        alert('Ссылка скопирована!');
      }
    } catch (error) {
      logger.log('Ошибка при шаринге:', error);
    }
  };

  const handleCheckoutSubmit = async (contactData) => {
    logger.log('🛒 handleCheckoutSubmit получил данные:', contactData);

    // Добавляем данные промокода и бонусов к контактным данным
    const enrichedContactData = {
      ...contactData,
      promoCode: promoDiscount > 0 ? promoCode : null,
      promoDiscount: promoDiscount,
      balanceToUse: useBalance ? balanceToUse : 0
    };

    // Вызываем внешний обработчик с дополнительными данными
    if (onCheckout) {
      await onCheckout(enrichedContactData);
      setShowCheckoutForm(false);
      setShowOrderConfirmation(true);
    }
  };

  const handleCloseOrderConfirmation = () => {
    setShowOrderConfirmation(false);
    // Очищаем корзину после успешного оформления заказа
    if (onClearCart) onClearCart();
  };


  // Добавляем стили для анимаций
  const styles = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes scaleIn {
    from { transform: scale(0.95); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }

  .animate-fadeIn {
    animation: fadeIn 0.2s ease-out;
  }

  .animate-scaleIn {
    animation: scaleIn 0.2s ease-out;
  }
  `;

  return (
    <div className="w-full min-h-screen">
      {/* Глобальный header — на всю ширину */}
      <GlobalHeader
        location="Москва и МО"
        currentPage={currentPage}
        onNavigate={onNavigate}
        cartItemsCount={cartItemsCount}
      />

      <div className="flex flex-col items-center w-full max-w-[1200px] mx-auto">
      <style dangerouslySetInnerHTML={{ __html: styles }} />

      {/* Контент с padding; pb-24 компенсирует fixed BottomNavigation */}
      <div className="w-full px-4 xl:px-6 pb-24 xl:pb-0">

      {/* Заголовок */}
      <h1 className="text-3xl font-bold mb-2 mt-4 text-black dark:text-white font-montserrat flex items-center justify-center">
        <CartIcon size="xlarge" className="mr-1" />
        <span>Корзина</span>
      </h1>

      {cartItems.length > 0 ? (
        <>
          {/* Информация о бонусах и доставке */}
          <CartBonusSection
            totalSum={totalSum}
            userBalance={userBalance}
            isFirstOrder={isFirstOrder}
          />

          {/* Список товаров в корзине */}
          <div
            className="w-full backdrop-blur-[4px] bg-gradient-to-br from-cyan-50/[0.10] to-blue-50/[0.03] dark:from-cyan-400/[0.10] dark:to-blue-400/[0.03] border border-white/[0.15] dark:border-white/[0.12] rounded-2xl shadow-md p-4 mb-6"
            style={{
              WebkitBackdropFilter: 'blur(16px)',
              backdropFilter: 'blur(16px)',
              boxShadow: '0 8px 24px rgba(99,102,241,0.15), inset 0 1px 0 rgba(255,255,255,0.15)'
            }}
          >
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Ваша корзина</h2>

            {normalizedCartItems.map(item => (
              <CartItem
                key={item.id}
                item={item}
                onUpdateQuantity={handleUpdateQuantity}
                onRemove={handleRemoveItem}
              />
            ))}

            <div className="mt-6 border-t border-white/[0.10] dark:border-white/[0.08] pt-4">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                <span>Товары</span>
                <span>{cartUtils.formatPrice(totalSum)}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                <span>Доставка</span>
                <span>{getDeliveryFee(totalSum).fee === 0 ? 'Бесплатно' : cartUtils.formatPrice(getDeliveryFee(totalSum).fee)}</span>
              </div>
              {promoDiscount > 0 && (
                <div className="flex justify-between text-sm text-emerald-600 dark:text-emerald-400 mb-2">
                  <span>Промокод -{promoDiscount}%</span>
                  <span>-{cartUtils.formatPrice(Math.round(totalSum * promoDiscount / 100))}</span>
                </div>
              )}
              {useBalance && balanceToUse > 0 && (
                <div className="flex justify-between text-sm text-sky-600 dark:text-sky-400 mb-2">
                  <span>Бонусы</span>
                  <span>-{cartUtils.formatPrice(balanceToUse)}</span>
                </div>
              )}
              <div className="flex justify-between text-xl font-bold text-gray-900 dark:text-gray-100 border-t border-white/[0.10] dark:border-white/[0.08] pt-2">
                <span>Итого</span>
                <span>{cartUtils.formatPrice(
                  totalSum
                  - Math.round(totalSum * promoDiscount / 100)
                  + getDeliveryFee(totalSum).fee
                  - (useBalance ? balanceToUse : 0)
                )}</span>
              </div>
            </div>
          </div>


          {/* Поле ввода промокода */}
          <div
            className="w-full backdrop-blur-[4px] bg-gradient-to-br from-cyan-50/[0.10] to-blue-50/[0.03] dark:from-cyan-400/[0.10] dark:to-blue-400/[0.03] border border-white/[0.15] dark:border-white/[0.12] rounded-2xl shadow-md p-4 mb-6"
            style={{
              WebkitBackdropFilter: 'blur(16px)',
              backdropFilter: 'blur(16px)',
              boxShadow: '0 8px 24px rgba(99,102,241,0.15), inset 0 1px 0 rgba(255,255,255,0.15)'
            }}
          >
            <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-gray-100">Есть промокод?</h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value);
                  setPromoError('');
                  // Сбрасываем скидку при изменении промокода
                  if (promoDiscount > 0) {
                    setPromoDiscount(0);
                  }
                }}
                placeholder="Введите промокод"
                className="flex-1 px-4 py-2 backdrop-blur-[4px] bg-white/[0.30] dark:bg-gray-800/[0.30] border border-white/[0.20] dark:border-white/[0.15] text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 placeholder:text-gray-500"
                style={{
                  WebkitBackdropFilter: 'blur(12px)',
                  backdropFilter: 'blur(12px)'
                }}
              />
              <button
                onClick={async () => {
                  if (promoCode && !isCheckingPromo) {
                    setIsCheckingPromo(true);
                    setPromoError('');

                    try {
                      // Проверяем промокод через API
                      const response = await botAPI.checkPromoCode(promoCode);

                      if (response.valid) {
                        setPromoDiscount(response.discount_percent);
                        setPromoError(''); // Очищаем ошибку
                      } else {
                        setPromoError(response.message || 'Недействительный промокод');
                        setPromoCode('');
                        setPromoDiscount(0);
                      }
                    } catch (error) {
                      console.error('Ошибка проверки промокода:', error);
                      setPromoError('Ошибка при проверке промокода');
                      setPromoDiscount(0);
                    } finally {
                      setIsCheckingPromo(false);
                    }
                  }
                }}
                className="w-full sm:w-auto bg-gradient-to-br from-blue-600/[0.80] to-indigo-600/[0.70] hover:from-cyan-500/[0.90] hover:to-blue-600/[0.80] text-white font-medium px-6 py-2 rounded-lg transition-all disabled:opacity-50"
                style={{
                  boxShadow: '0 4px 12px rgba(99,102,241,0.25)'
                }}
                disabled={isCheckingPromo}
              >
                {isCheckingPromo ? 'Проверяем...' : 'Применить'}
              </button>
            </div>
            {promoError && (
              <p role="alert" className="text-red-500 text-sm mt-2">{promoError}</p>
            )}
            {promoCode && promoDiscount > 0 && (
              <p className="text-green-600 text-sm mt-2">✓ Промокод применен! Скидка {promoDiscount}%</p>
            )}
          </div>

          {/* Секция списания бонусов */}
          {userBalance > 0 && (
            <div
              className="w-full backdrop-blur-[4px] bg-gradient-to-br from-sky-50/[0.10] to-blue-50/[0.03] dark:from-sky-400/[0.10] dark:to-blue-400/[0.03] border border-white/[0.15] dark:border-white/[0.12] rounded-2xl shadow-md p-4 mb-6"
              style={{
                WebkitBackdropFilter: 'blur(16px)',
                backdropFilter: 'blur(16px)',
                boxShadow: '0 8px 24px rgba(14,165,233,0.15), inset 0 1px 0 rgba(255,255,255,0.15)'
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Списать бонусы</h3>
                <button
                  onClick={() => {
                    setUseBalance(!useBalance);
                    if (!useBalance) setBalanceToUse(maxBalanceAllowed);
                  }}
                  className={`relative w-12 h-7 rounded-full transition-colors duration-200 ${useBalance ? 'bg-sky-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-200 ${useBalance ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>

              {useBalance && (
                <>
                  <input
                    type="range"
                    min={0}
                    max={maxBalanceAllowed}
                    step={1}
                    value={balanceToUse}
                    onChange={(e) => setBalanceToUse(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-sky-500"
                  />
                  <div className="flex justify-between mt-2 text-sm text-gray-600 dark:text-gray-400">
                    <span>Списать: <span className="font-semibold text-sky-600 dark:text-sky-400">{cartUtils.formatPrice(balanceToUse)}</span></span>
                    <span>из {cartUtils.formatPrice(userBalance)} (макс. 30%)</span>
                  </div>
                </>
              )}

              {!useBalance && (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  На балансе: <span className="font-semibold">{cartUtils.formatPrice(userBalance)}</span>
                </div>
              )}
            </div>
          )}

          {/* Рекомендуемые товары */}
          <RecommendationBanner
            cartItems={cartItems}
            onAddToCart={handleAddToCart}
            allProducts={allProducts}
          />

          {/* Кнопка оформления заказа - сразу после промокода */}
          <div className="w-full mb-6">
            <button
              onClick={handleCheckout}
              className="w-full bg-gradient-to-br from-green-600/[0.70] to-emerald-600/[0.60] hover:from-green-600/[0.85] hover:to-emerald-600/[0.75] text-white font-bold py-4 px-4 rounded-lg flex items-center justify-center text-xl shadow-lg hover:shadow-xl transition-all"
              style={{
                boxShadow: '0 8px 24px rgba(34,197,94,0.25)'
              }}
            >
              <CartIcon size="medium" className="mr-2 inline-block" />
              Оформить заказ
            </button>
          </div>

        </>
      ) : (
        // Пустая корзина
        <div
          className="w-full backdrop-blur-[4px] bg-gradient-to-br from-cyan-50/[0.10] to-blue-50/[0.03] dark:from-cyan-400/[0.10] dark:to-blue-400/[0.03] border border-white/[0.15] dark:border-white/[0.12] rounded-2xl shadow-md p-8"
          style={{
            WebkitBackdropFilter: 'blur(16px)',
            backdropFilter: 'blur(16px)',
            boxShadow: '0 8px 24px rgba(99,102,241,0.15), inset 0 1px 0 rgba(255,255,255,0.15)'
          }}
        >
          <CharacterMessage
            character="fish-professor"
            title="Ваша корзина пуста"
            message="Начните с каталога! Выберите свежие морепродукты и добавьте их в корзину."
            action={onGoToCatalog}
            actionText="Перейти в каталог"
            size="large"
          />
        </div>
      )}

      {/* Модальное окно формы оформления заказа */}
      <CheckoutForm
        isVisible={showCheckoutForm}
        onClose={() => setShowCheckoutForm(false)}
        onSubmit={handleCheckoutSubmit}
        telegramUser={telegramUser}
        savedContact={savedContact}
      />

      {/* Модальное окно подтверждения заказа */}
      <OrderConfirmation
        isVisible={showOrderConfirmation}
        onClose={handleCloseOrderConfirmation}
        cartItems={cartItems}
        totalSum={totalSum}
      />

      {/* Модальное окно для гостя — предложение авторизации */}
      {showGuestAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowGuestAuthPrompt(false)} role="dialog" aria-modal="true" aria-labelledby="guest-auth-prompt-title">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <img src={new URL('./assets/icons/fish-logo.png', import.meta.url).href} alt="" className="w-12 h-12 mx-auto mb-3 object-contain" />
              <h3 id="guest-auth-prompt-title" className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
                Нужна авторизация
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-5">
                Для оформления заказа войдите в аккаунт — так мы сможем связаться с вами и сохранить историю заказов
              </p>
              <button
                onClick={() => {
                  sessionStorage.removeItem('guest_mode');
                  if (onNavigate) {
                    onNavigate('login');
                  } else {
                    window.location.href = '/login';
                  }
                }}
                className="w-full bg-sky-500 hover:bg-sky-600 text-white font-bold py-3 px-4 rounded-xl transition-colors cursor-pointer mb-2"
              >
                Войти или зарегистрироваться
              </button>
              <button
                onClick={() => setShowGuestAuthPrompt(false)}
                className="w-full text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-400 py-2 transition-colors"
              >
                Назад к корзине
              </button>
            </div>
          </div>
        </div>
      )}

      </div>{/* закрытие .px-4 контента */}
      </div>{/* закрытие .max-w-4xl */}
    </div>
  );
};

export default CartComponent;
